import type { ReactElement } from "react";
import { apiBase, createClient } from "../../api/client";
import { localTokenStore } from "../../api/session";
import { KioskScreen } from "./KioskScreen";
import { createKioskEngine } from "./engine";
import { kioskHost } from "./host";

/** `/kiosk` exactly: setup and exit live below it and belong to the staff app. */
export const isKioskModePath = (pathname: string) => pathname === "/kiosk" || pathname === "/kiosk/";

/**
 * A kiosk device never holds a staff token. If one is still in storage (a staff member signed in on the device, then went
 * straight to /kiosk), it is removed before anything renders and logged out on the server in the background.
 */
export function purgeStaffSession(): void {
  const tokens = localTokenStore();
  const stored = tokens.read();
  if (!stored) return;
  tokens.write(null);
  const stale = stored.token;
  void createClient({ baseUrl: apiBase, getToken: () => stale, onUnauthenticated: () => {} })
    .post("/auth/logout")
    .catch(() => undefined);
}

/**
 * Called by `main.tsx` before the staff app mounts. Returns the kiosk screen when this device is a kiosk and the path is
 * `/kiosk`; `null` hands over to the normal app. Kiosk mode has no router, no shell, no sidebar and no staff token.
 */
export async function bootKiosk(pathname: string = window.location.pathname): Promise<ReactElement | null> {
  if (!isKioskModePath(pathname)) return null;
  const host = kioskHost();
  const credential = await host.store.read("credential").catch(() => null);
  if (!credential) return null;
  purgeStaffSession();
  const engine = createKioskEngine({ baseUrl: apiBase, credential, store: host.store });
  return <KioskScreen engine={engine} />;
}
